import React, { useState } from 'react';
import { Settings2 } from 'lucide-react';
import { PageHeader } from '@/components/blocks/page-header';
import { Button } from '@/components/ui/button';
import { Tabs } from '@/components/ui/tabs';
import { ValidationErrorSummary } from '@/components/settings/ValidationErrorSummary';
import { HealthCheckBadge } from '@/components/settings/HealthCheckBadge';
import type { ValidationError } from '@/hooks/useSettingsValidation';
import type { HealthCheckFinding } from '@/types/healthCheck';
import { toast } from 'sonner';
import { usePermissions } from '@/contexts/PermissionContext';
import { useCompany } from '@/features/multi-company/context/CompanyContext';
import { useOvertimeTypes } from '@/hooks/useOvertimeTypes';
import { SettingsTabsList } from './SettingsPage/components/SettingsTabsList';
import { SettingsFooterBar } from './SettingsPage/components/SettingsFooterBar';
import { SettingsPageState } from './SettingsPage/components/SettingsPageStates';
import { SettingsPageDialogs } from './SettingsPage/components/SettingsPageDialogs';
import { useSettingsFormState } from './SettingsPage/hooks/useSettingsFormState';
import { useSettingsData } from './SettingsPage/hooks/useSettingsData';
import { useSettingsSave } from './SettingsPage/hooks/useSettingsSave';
import { applyHealthCheckFix } from './SettingsPage/utils/applyHealthCheckFix';
import { GeneralTab } from './SettingsPage/tabs/GeneralTab';
import { WorkingHoursTab } from './SettingsPage/tabs/WorkingHoursTab';
import { SubmissionsDeadlinesTab } from './SettingsPage/tabs/SubmissionsDeadlinesTab';
import { OvertimeTab } from './SettingsPage/tabs/OvertimeTab';
import { UtilisationTab } from './SettingsPage/tabs/UtilisationTab';

interface SettingsPageProps {
  onNavigate?: (path: string) => void;
  initialTab?: string;
}

export const SettingsPage: React.FC<SettingsPageProps> = ({ onNavigate, initialTab }) => {
  // Permission gates — each controls one tab's editability
  const {
    canManageSystemSettings,
    canManagePolicySettings,
    canManageDeadlines,
    canManageUtilisationSettings,
    canManageTimeEntrySettings,
    canManageWorkTypeSettings,
  } = usePermissions();

  // canEdit[tab] = true means the user can save changes on that tab
  // canManageSystemSettings() is the master switch — if false, all tabs are read-only
  const canEditWorkingHours     = canManageSystemSettings() && canManagePolicySettings();
  const canEditDeadlines        = canManageSystemSettings() && canManageDeadlines();
  const canEditTimeEntryRules   = canManageSystemSettings() && canManageTimeEntrySettings();
  const canEditWorkType         = canManageSystemSettings() && canManageWorkTypeSettings();
  const canEditUtilisation      = canManageSystemSettings() && canManageUtilisationSettings();

  const form = useSettingsFormState();
  const {
    deadlineSettings, utilisationSettings, workingProfile,
    policyEnforcement, policyLimits,
    hasUnsavedChanges, tabsWithChanges,
    setDeadlineSettings, setUtilisationSettings, setWorkingProfile, setPolicyLimits,
    setShowRole, setMinimumEntryDuration, setAllowFutureDatedEntries, setEnableAiInsights,
    setHistoricalEntryLimit, setAllowNegativeHours, setEnableCopyPreviousWeek,
    setHasUnsavedChanges, setTabsWithChanges, markTabAsChanged, hydrate,
  } = form;

  const [activeTab, setActiveTab] = useState<string>(initialTab ?? 'working-hours');

  // Validation state (shared with the data hook's conflict handling and the save hook)
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);

  const data = useSettingsData({
    hydrate,
    workingProfile,
    setWorkingProfile,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    setTabsWithChanges,
    setValidationErrors,
  });
  const {
    fullSettings,
    isLoading, error, isSaving, lastSaved,
    healthCheckResult, showHealthCheckDialog,
    showConflictDialog, conflictServerSettings,
    setShowConflictDialog, setShowHealthCheckDialog,
    loadData, handleConflictResolve, handleRunHealthCheck,
  } = data;

  // Company context and overtime types from API
  const { currentCompany } = useCompany();
  const companyId = currentCompany?.id;
  const { overtimeTypes, refetch: refetchOvertimeTypes } = useOvertimeTypes({ includeInactive: true, screenId: 'settings-general' });

  const save = useSettingsSave({ form, data, setValidationErrors });

  if (isLoading) {
    return <SettingsPageState variant="loading" onRetry={loadData} />;
  }

  if (error) {
    return <SettingsPageState variant="error" error={error} onRetry={loadData} />;
  }

  if (!deadlineSettings || !utilisationSettings || !workingProfile || !policyEnforcement) {
    return <SettingsPageState variant="missing" onRetry={loadData} />;
  }

  // Handle applying a quick fix from health check
  const handleApplyHealthCheckFix = (finding: HealthCheckFinding) => {
    applyHealthCheckFix(finding, {
      deadlineSettings,
      setDeadlineSettings,
      utilisationSettings,
      setUtilisationSettings,
      policyLimits,
      setPolicyLimits,
      setShowRole,
      setEnableCopyPreviousWeek,
      setHistoricalEntryLimit,
      setAllowNegativeHours,
      setEnableAiInsights,
      setMinimumEntryDuration,
      setAllowFutureDatedEntries,
      markTabAsChanged,
      setActiveTab,
      setShowHealthCheckDialog,
      toast,
    });
  };

  const activeTabBlocked = (() => {
    switch (activeTab) {
      case 'working-hours':       return !canEditWorkingHours;
      case 'submissions-deadlines': return !canEditDeadlines;
      case 'general':             return !canEditTimeEntryRules;
      case 'overtime':            return !canEditWorkType;
      case 'utilisation':         return !canEditUtilisation;
      default:                    return false;
    }
  })();

  return (
    <div className="space-y-6 pb-24">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <PageHeader
          title="General Settings"

          size={36}
          showActions={false}
          showBreadcrumb={false}
          icon={<Settings2 />}
        />
        <div className="flex items-center gap-3 pt-2">
          {healthCheckResult && (
            <HealthCheckBadge 
              score={healthCheckResult.score} 
              onClick={() => setShowHealthCheckDialog(true)}
            />
          )}
          <Button 
            variant="outline" 
            onClick={handleRunHealthCheck}
            className="gap-2"
          >
            <Settings2 className="h-4 w-4" />
            Run Health Check
          </Button>
        </div>
      </div>

      {/* Tabbed Interface */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <SettingsTabsList tabsWithChanges={tabsWithChanges} />

        {/* Validation Error Summary */}
        {validationErrors.length > 0 && (
          <ValidationErrorSummary
            errors={validationErrors}
            onDismiss={() => setValidationErrors([])}
            onNavigateToError={(tabId) => setActiveTab(tabId)}
          />
        )}

        {/* Time Entry - Data Requirements */}
        <GeneralTab form={form} canEditTimeEntryRules={canEditTimeEntryRules} />

        {/* Working Hours & Policies */}
        <WorkingHoursTab
          form={form}
          workingProfile={workingProfile}
          policyEnforcement={policyEnforcement}
          canEditWorkingHours={canEditWorkingHours}
          canManagePolicySettings={canManagePolicySettings()}
          onBeforeSync={save.handleBeforeSync}
        />

        {/* Submissions & Deadlines */}
        <SubmissionsDeadlinesTab
          form={form}
          deadlineSettings={deadlineSettings}
          canEditDeadlines={canEditDeadlines}
          onNavigate={onNavigate}
        />

        {/* Work Type */}
        <OvertimeTab
          form={form}
          canEditWorkType={canEditWorkType}
          companyId={companyId}
          overtimeTypes={overtimeTypes}
          refetchOvertimeTypes={refetchOvertimeTypes}
        />

        {/* Utilisation */}
        <UtilisationTab
          form={form}
          utilisationSettings={utilisationSettings}
          canEditUtilisation={canEditUtilisation}
        />
      </Tabs>

      <SettingsFooterBar
        lastSaved={lastSaved}
        hasUnsavedChanges={hasUnsavedChanges}
        isSaving={isSaving}
        onCancel={save.handleCancel}
        onSave={save.handleSave}
        saveDisabled={activeTabBlocked}
        saveDisabledReason={activeTabBlocked ? 'You do not have permission to edit this settings tab' : undefined}
      />

      <SettingsPageDialogs
        showCancelDialog={save.showCancelDialog}
        setShowCancelDialog={save.setShowCancelDialog}
        onConfirmCancel={save.handleConfirmCancel}
        showConflictDialog={showConflictDialog}
        conflictServerSettings={conflictServerSettings}
        fullSettings={fullSettings}
        onConflictResolve={handleConflictResolve}
        onConflictCancel={() => setShowConflictDialog(false)}
        showHealthCheckDialog={showHealthCheckDialog}
        healthCheckResult={healthCheckResult}
        onHealthCheckClose={() => setShowHealthCheckDialog(false)}
        onApplyHealthCheckFix={handleApplyHealthCheckFix}
      />
    </div>
  );
};
